import { useCallback, useEffect, useState } from "react";
import PerfectScrollbar from "react-perfect-scrollbar";
import { useFormik } from "formik";

import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  FormControlLabel,
  FormGroup,
  FormLabel,
  LinearProgress,
  TextField,
  Typography,
} from "@material-ui/core";

import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import MaskAndValid from "utils/maskAndValid";
import Api from "lib/api/httpRequestHelper";

export default function FormReasonDialog({
  open,
  formType,
  onClose,
  selectedReason,
}) {
  const snackbar = useSnackbar();

  const [submitting, setSubmitting] = useState(false);

  const submit = async (values) => {
    const { motivo, ativo, validaVolume, requiredEvidence, requiredWeightAndSpecies } = values;
    const updatedReason = {};

    setSubmitting(true);

    if (formType === "delete") {
      setSubmitting(true);

      const idMotivo = selectedReason?.IdMotivo;

      await Api.delete(
        "delete-reason",
        "trocasDevolucoes",
        { idMotivo: idMotivo },
        true
      )
        .then((res) => {
          if (res.Success) {
            snackbar.showMessage(`Motivo excluído com sucesso!`, {
              severity: "success",
            });
            setSubmitting(false);
            handleClose({ idMotivo });
          } else {
            snackbar.showMessage("Ocorreu um erro ao deletar o motivo.", {
              severity: "error",
            });
            setSubmitting(false);
          }
        })
        .catch((error) => {
          snackbar.showMessage(error, {
            severity: "error",
          });
          setSubmitting(false);        });
    }

    if (formType === "edit") {
      return await Api.put(
        "edit-reason",
        "trocasDevolucoes",
        {
          motivo: motivo,
          idMotivo: selectedReason.IdMotivo,
          status: ativo === true ? 1 : 0,
          validaVolume: validaVolume,
          obrigaPesoEspecie: requiredWeightAndSpecies,
          obrigaEvidencia: requiredEvidence,
        },
        true
      )
        .then((res) => {
          if (res.Success) {
            snackbar.showMessage(`Motivo atualizado com sucesso!`, {
              severity: "success",
            });
            setSubmitting(false);
            handleClose({ ...updatedReason, ...res.Result });
          } else {
            snackbar.showMessage(
              `Ocorreu um erro ao atualizar o motivo. Verifique os parâmetros informados.`,
              {
                severity: "error",
              }
            );
            setSubmitting(false);
          }
        })
        .catch((error) => {
          snackbar.showMessage(error, {
            severity: "error",
          });
          setSubmitting(false);        });
    }

    if (formType === "create") {
      const body = {
        motivo: motivo,
        validaVolume: validaVolume,
        obrigaPesoEspecie: requiredWeightAndSpecies,
        obrigaEvidencia: requiredEvidence,
      };
      return await Api.post(
        "create-reason",
        "trocasDevolucoes",
        body,
        true
      )
        .then((res) => {
          if (res.Success) {
            snackbar.showMessage(`Motivo criado com sucesso!`, {
              severity: "success",
            });
            setSubmitting(false);
            handleClose({ ...updatedReason, ...res.Result });
          } else {
            snackbar.showMessage(
              `Ocorreu um erro ao criar o motivo. Verifique os parâmetros informados.`,
              {
                severity: "error",
              }
            );
            setSubmitting(false);
          }
        })
        .catch((error) => {
          snackbar.showMessage(error, {
            severity: "error",
          });
          setSubmitting(false);        });
    }

    return;
  };

  useEffect(() => {
    const handleActiveToggle = async () => {
      if (formType === "active" && selectedReason) {
        const ativo = selectedReason.Status;

        setSubmitting(true);

        try {
          const res = await Api.put(
            "edit-reason",
            "trocasDevolucoes",
            {
              motivo: selectedReason?.Motivo,
              idMotivo: selectedReason.IdMotivo,
              status: ativo === 0 ? 1 : 0,
            },
            true
          );

          if (res.Success) {
            snackbar.showMessage(
              `Motivo ${selectedReason.IdMotivo.toString()} foi ${
                ativo === 1 ? "desativado" : "ativado"
              } com sucesso!`,
              {
                severity: "success",
              }
            );
          } else {
            snackbar.showMessage(
              `Ocorreu um erro ao ${
                ativo === 1 ? "desativar" : "ativar"
              } Motivo ${selectedReason.IdMotivo.toString()}. Verifique os parâmetros informados.`,
              {
                severity: "error",
              }
            );
          }
        } catch (error) {
          snackbar.showMessage(error, {
            severity: "error",
          });        } finally {
          setSubmitting(false);
          handleClose();
        }
      }
    };

    handleActiveToggle();
  }, [formType, selectedReason]);

  const formik = useFormik({
    initialValues: {
      motivo: selectedReason?.Motivo || "",
      ativo: selectedReason?.Status === 1 ? true : false,
      validaVolume: selectedReason?.ValidaVolume === true || selectedReason?.ValidaVolume === "true",
      requiredEvidence: selectedReason?.ObrigaEvidencia === true || selectedReason?.RequiredEvidence === 1 ? true : false,
      requiredWeightAndSpecies: selectedReason?.ObrigaPesoEspecie === true || selectedReason?.RequiredWeightAndSpecies === 1 ? true : false,
    },
    onSubmit: submit,
  });

  const { values, handleChange, handleSubmit, dirty, resetForm, setFieldValue } = formik;

  const handleClose = useCallback(
    (updatedReason) => {
      resetForm();
      onClose(updatedReason);
    },
    [onClose, resetForm]
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        maxWidth={"xl"}
      >
        <PerfectScrollbar>
          <DialogTitle>
            <span>
              <Typography id="alert-dialog-title" variant="h3" component="h3">
                {`${
                  formType === "edit"
                    ? "Editar"
                    : formType === "delete"
                    ? "Excluir"
                    : formType === "active"
                    ? selectedReason.Status === 1
                      ? "Inativar"
                      : "Ativar"
                    : "Criar"
                } Motivo`}
              </Typography>
            </span>
          </DialogTitle>
          <PerfectScrollbar>
            <DialogContent style={{ padding: "1rem 1.5rem 0" }}>
              {formType === "delete" ? (
                <>
                  <Typography variant="h4" style={{ marginBottom: "1rem" }}>
                    Confirmar exclusão do Motivo?
                  </Typography>
                </>
              ) : formType === "active" ? (
                <>
                  <Typography variant="h4" style={{ marginBottom: "1rem" }}>
                    {`Confirmar ${
                      selectedReason.Status === 1 ? "inativação" : "ativação"
                    } do Motivo?`}
                  </Typography>
                </>
              ) : (
                <>
                  <Grid item xs={12}>
                    <FormGroup>
                      <FormLabel>Motivo *</FormLabel>
                      <TextField
                        name="motivo"
                        required
                        fullWidth
                        disabled={submitting}
                        value={values.motivo}
                        variant="outlined"
                        onChange={handleChange}
                        onInput={(e) => {
                          e.target.value = e.target.value
                            .toString()
                            .slice(0, 200);
                        }}
                      />
                    </FormGroup>
                  </Grid>
                  <Box style={{ display: "flex", flexWrap: "wrap", gap: "1rem", marginTop: "1rem" }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={values.validaVolume}
                          onChange={handleChange}
                          name="validaVolume"
                          color="primary"
                          disabled={submitting}
                        />
                      }
                      label={<FormLabel>Valida volume</FormLabel>}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={values.requiredEvidence}
                          onChange={handleChange}
                          name="requiredEvidence"
                          color="primary"
                          disabled={submitting}
                        />
                      }
                      label={<FormLabel>Obriga informar evidência</FormLabel>}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={values.requiredWeightAndSpecies}
                          onChange={handleChange}
                          name="requiredWeightAndSpecies"
                          color="primary"
                          disabled={submitting}
                        />
                      }
                      label={<FormLabel>Obriga informar peso e espécie</FormLabel>}
                    />
                  </Box>
                  {formType === "edit" && (
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={values.ativo}
                            onChange={handleChange}
                            name="ativo"
                            color="primary"
                            disabled={submitting}
                          />
                        }
                        label={<FormLabel>Ativo</FormLabel>}
                      />
                    </Grid>
                  )}
                </>
              )}
              {submitting && (
                <div style={{ marginTop: "1rem" }}>
                  <span>{`${
                    formType === "create"
                      ? "Criando"
                      : formType === "edit"
                      ? "Atualizando"
                      : formType === "active"
                      ? selectedReason.Status === 1
                        ? "Inativando"
                        : "Ativando"
                      : "Deletando"
                  } Motivo...`}</span>
                  <LinearProgress />
                </div>
              )}
            </DialogContent>
          </PerfectScrollbar>
          <DialogActions>
            <Button component={Box} onClick={handleClose} disabled={submitting}>
              Cancelar
            </Button>
            {formType === "delete" ? (
              <Button
                color="default"
                variant="contained"
                type="submit"
                disabled={submitting}
                onClick={submit}
              >
                Confirmar
              </Button>
            ) : formType === "active" ? (
              <Button
                color="default"
                variant="contained"
                type="submit"
                disabled={submitting}
                onClick={submit}
              >
                {selectedReason.Status == 1 ? "Inativar" : "Ativar"}
              </Button>
            ) : (
              <Button
                color="default"
                variant="contained"
                type="submit"
                disabled={
                  submitting ||
                  (formType !== "edit" && !dirty) ||
                  MaskAndValid.isNullOrEmpty(values.motivo)
                }
                onClick={handleSubmit}
              >
                {formType === "edit" ? "Atualizar" : "Criar"}
              </Button>
            )}
          </DialogActions>
        </PerfectScrollbar>
      </Dialog>
    </>
  );
}
